import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { TESTIMONIALS } from "../data/content";
import { fadeUp, viewport } from "../lib/motion";

const Testimonials = () => {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const t = TESTIMONIALS[i];

  const go = (d) => {
    setDir(d);
    setI((p) => (p + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  return (
    <section className="relative py-16 sm:py-32" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-primary mb-5 flex items-center justify-center gap-3"
        >
          <span className="h-px w-8 bg-highlight" />
          Testimonials
          <span className="h-px w-8 bg-highlight" />
        </motion.p>

        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-7"
        >
          Loved by <span className="text-highlight">the neighborhood.</span>
        </motion.h2>

        <div className="flex flex-col items-center gap-3 mb-10">
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, s) => (
              <Star key={s} size={18} className="fill-highlight text-highlight" />
            ))}
          </div>
          <p className="text-sm text-foreground/60" data-testid="google-rating">
            <span className="font-semibold text-foreground">4.5</span> on Google · 480+ reviews
          </p>
        </div>

        <div className="relative min-h-[240px] sm:min-h-[220px] flex items-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.blockquote
              key={i}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
              data-testid={`testimonial-${i}`}
            >
              <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-foreground">
                “{t.quote}”
              </p>
              <footer className="mt-8">
                <p className="font-semibold text-foreground">{t.name}</p>
                <p className="text-sm text-foreground/55">{t.role}</p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            onClick={() => go(-1)}
            data-testid="testimonial-prev"
            aria-label="Previous testimonial"
            className="h-11 w-11 rounded-full border border-surface flex items-center justify-center text-foreground transition-colors hover:bg-primary hover:text-background hover:border-primary"
          >
            <ArrowLeft size={18} strokeWidth={1.6} />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setDir(idx > i ? 1 : -1); setI(idx); }}
                aria-label={`Go to testimonial ${idx + 1}`}
                data-testid={`testimonial-dot-${idx}`}
                className={`h-2 rounded-full transition-all duration-300 ${idx === i ? "w-7 bg-primary" : "w-2 bg-surface"}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            data-testid="testimonial-next"
            aria-label="Next testimonial"
            className="h-11 w-11 rounded-full border border-surface flex items-center justify-center text-foreground transition-colors hover:bg-primary hover:text-background hover:border-primary"
          >
            <ArrowRight size={18} strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
